import { Component, PluginFunction } from 'vue';
import humanId from 'human-id';
import ErrorDialog from '@/components/shared/error-dialog/ErrorDialog.vue';
import CustomDialog from '@/components/shared/custom-dialog/CustomDialog.vue';
import Toast from '@/components/shared/toast/Toast.vue';
import { applyPolyfills, defineCustomElements } from '@camelot/web-components/dist/loader';

const basePlugin = ((document) => (id: string, plugin: (id: string) => any) => {
  const appContainer = document.getElementById('app');

  if (!appContainer) {
    return;
  }

  let slot = document.getElementById(id);
  if (slot) {
    return;
  }

  slot = document.createElement('div');
  slot.setAttribute('id', `${id}`);
  appContainer.appendChild(slot);

  return plugin(`#${id}`);
})(document);

export const SpaUtils: PluginFunction<any> = (Vue: any) => {
  Vue.config.ignoredElements = [/camelot-\w*/];
  applyPolyfills().then(() => {
    defineCustomElements(window);
  });

  Vue.utils = {
    $notify: (title: string, message: string, onClose = () => null) =>
      basePlugin('dialog-box-slot', (el: string) => {
        const errorUuid = humanId({ separator: ' ' }).toUpperCase();
        const ErrorDialogConstructor = Vue.extend(ErrorDialog);
        const errorDialog = new ErrorDialogConstructor({
          el,
          propsData: {
            message,
            title,
            errorUuid,
          },
        });
        errorDialog.$on('close-dialog', () => {
          errorDialog.$destroy();
          (errorDialog.$el.parentNode as HTMLElement).removeChild(errorDialog.$el);
          onClose();
        });
        errorDialog.$mount();

        return errorUuid;
      }),
    $customNotify: (DialogComponent: Component, propsData: object, onClose = () => null) =>
      basePlugin('dialog-box-slot', (el: string) => {
        const DialogComponentConstructor = Vue.extend(DialogComponent);
        const dialogComponent = new DialogComponentConstructor({
          el,
          propsData,
        });
        dialogComponent.$on('close-dialog', () => {
          dialogComponent.$destroy();
          (dialogComponent.$el.parentNode as HTMLElement).removeChild(dialogComponent.$el);
          onClose();
        });
        dialogComponent.$mount();
      }),
    $dialog: (slotComponent: Component, propsData: object, props: object = {}, onClose = () => null) =>
      basePlugin('dialog-box-slot', (el: string) => {
        const CustomDialogConstructor = Vue.extend(CustomDialog);
        const customDialog = new CustomDialogConstructor({
          el,
          propsData: {
            ...props,
            slotComponent,
            propsData,
          },
        });
        customDialog.$on('close-dialog', () => {
          customDialog.$destroy();
          (customDialog.$el.parentNode as HTMLElement).removeChild(customDialog.$el);
          onClose();
        });
        customDialog.$mount();

        return customDialog;
      }),
    $toast: (message: string, type: string = 'error', autoClose: boolean = true) =>
      basePlugin('toast', (el: string) => {
        const toast = new (Toast as any)({
          el,
          propsData: {
            autoClose,
            message,
            type,
          },
        });
        toast.$on('close-toast', () => {
          toast['destroy']();
        });
        toast.$mount();

        return toast;
      }),
  };
};
